import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  MoneyOff,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import TabDescription from './TabDescription';
import { useRegionContext } from '../../region/RegionContext';

// ── Helpers ─────────────────────────────────────────────────────────────────

const getStartOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
};

const getStartOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const weeksBetween = (d1, d2) => {
  const ms = Math.abs(d2 - d1);
  return Math.max(1, Math.ceil(ms / (7 * 24 * 60 * 60 * 1000)));
};

const formatCurrency = (amount) =>
  `$${(amount || 0).toFixed(2)}`;

const formatDate = (date) => {
  if (!date) return '—';
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getOrderTotal = (order) => {
  if (order.orderTotal) return order.orderTotal;
  let total = 0;
  for (const dish of order.dishes || []) {
    const count = dish.dishCount || 1;
    const price = dish.dishAvailSize?.[0]?.price || 0;
    total += count * price;
  }
  return total;
};

// ── Expandable Row ──────────────────────────────────────────────────────────

const VenueRow = ({ venue }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton
            size="small"
            aria-label={open ? 'Collapse venue details' : 'Expand venue details'}
            aria-expanded={open}
            aria-controls={`refund-details-${venue.bizId}`}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{venue.bizName}</TableCell>
        <TableCell align="right">{venue.totalRefunds}</TableCell>
        <TableCell align="right">{venue.refundsThisWeek}</TableCell>
        <TableCell align="right">{venue.avgRefundsPerWeek.toFixed(1)}</TableCell>
        <TableCell align="right">{formatCurrency(venue.totalRefundValue)}</TableCell>
        <TableCell>{formatDate(venue.lastRefundDate)}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit id={`refund-details-${venue.bizId}`}>
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Refunded Orders
              </Typography>
              <Table size="small" aria-label="Refunded orders for venue">
                <TableHead>
                  <TableRow>
                    <TableCell scope="col">Order Date</TableCell>
                    <TableCell scope="col">Dishes</TableCell>
                    <TableCell scope="col" align="right">Refund Amount</TableCell>
                    <TableCell scope="col">Stripe Refund ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {venue.orders.map((order, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{formatDate(order.date)}</TableCell>
                      <TableCell>
                        {(order.dishes || [])
                          .map((d) => `${d.dishCount || 1}x ${d.dishName || 'Unknown'}`)
                          .join(', ') || '—'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(order.total)}
                      </TableCell>
                      <TableCell>
                        {order.stripeRefundId || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

const RefundTracking = () => {
  const { db } = useRegionContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refundedOrders, setRefundedOrders] = useState([]);
  const [venues, setVenues] = useState({});
  const [dateFilter, setDateFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [sortField, setSortField] = useState('totalRefunds');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const ordersSnap = await db
        .collection('Orders')
        .where('OrderStatus', '==', 'REFUNDED')
        .limit(500)
        .get();

      const orders = ordersSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Only look up venues that have refunded orders, instead of loading ALL businesses
      const bizIds = [...new Set(orders.map(o => o.bizId).filter(Boolean))];
      const venueMap = {};
      // Batch lookup in chunks of 10 (Firestore 'in' query limit)
      for (let i = 0; i < bizIds.length; i += 10) {
        const chunk = bizIds.slice(i, i + 10);
        const snap = await db.collection('Business').where('bizId', 'in', chunk).get();
        snap.docs.forEach((doc) => {
          const d = doc.data();
          venueMap[d.bizId || doc.id] = {
            bizName: d.bizName || 'Unknown Venue',
            bizState: d.bizState || '',
          };
        });
      }

      setRefundedOrders(orders);
      setVenues(venueMap);
    } catch (err) {
      console.error('Error loading refund data:', err);
      setError('Failed to load refund data');
    } finally {
      setLoading(false);
    }
  };

  // ── Compute derived data ──────────────────────────────────────────────────

  const now = new Date();
  const weekStart = getStartOfWeek(now);
  const monthStart = getStartOfMonth(now);

  const filteredOrders = refundedOrders.filter((order) => {
    const orderDate = order.timestamp?.toDate?.() || null;
    if (!orderDate) return dateFilter === 'all';

    if (dateFilter === '7') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 7);
      if (orderDate < cutoff) return false;
    } else if (dateFilter === '30') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 30);
      if (orderDate < cutoff) return false;
    } else if (dateFilter === '90') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 90);
      if (orderDate < cutoff) return false;
    }

    if (stateFilter !== 'all') {
      const venue = venues[order.bizId];
      if (!venue || venue.bizState !== stateFilter) return false;
    }

    return true;
  });

  // Summary cards
  const totalRefunds = filteredOrders.length;
  const refundsThisWeek = filteredOrders.filter((o) => {
    const d = o.timestamp?.toDate?.();
    return d && d >= weekStart;
  }).length;
  const refundsThisMonth = filteredOrders.filter((o) => {
    const d = o.timestamp?.toDate?.();
    return d && d >= monthStart;
  }).length;
  const totalRefundValue = filteredOrders.reduce(
    (sum, o) => sum + getOrderTotal(o),
    0
  );

  // Per-venue aggregation
  const venueAgg = {};
  filteredOrders.forEach((order) => {
    const bizId = order.bizId || 'unknown';
    if (!venueAgg[bizId]) {
      const v = venues[bizId] || {};
      venueAgg[bizId] = {
        bizId,
        bizName: v.bizName || 'Unknown Venue',
        totalRefunds: 0,
        refundsThisWeek: 0,
        totalRefundValue: 0,
        firstRefundDate: null,
        lastRefundDate: null,
        orders: [],
      };
    }

    const agg = venueAgg[bizId];
    const orderDate = order.timestamp?.toDate?.() || null;

    agg.totalRefunds += 1;
    agg.totalRefundValue += getOrderTotal(order);

    if (orderDate && orderDate >= weekStart) {
      agg.refundsThisWeek += 1;
    }

    if (!agg.firstRefundDate || (orderDate && orderDate < agg.firstRefundDate)) {
      agg.firstRefundDate = orderDate;
    }
    if (!agg.lastRefundDate || (orderDate && orderDate > agg.lastRefundDate)) {
      agg.lastRefundDate = orderDate;
    }

    agg.orders.push({
      date: orderDate,
      dishes: order.dishes,
      total: getOrderTotal(order),
      stripeRefundId: order.stripeRefundId || order.refundId || '',
    });
  });

  const venueRows = Object.values(venueAgg).map((v) => ({
    ...v,
    avgRefundsPerWeek: v.firstRefundDate
      ? v.totalRefunds / weeksBetween(v.firstRefundDate, now)
      : v.totalRefunds,
  }));

  // Sort
  venueRows.sort((a, b) => {
    const valA = a[sortField];
    const valB = b[sortField];
    if (typeof valA === 'string') {
      return sortDir === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortDir === 'asc' ? valA - valB : valB - valA;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Available states for filter
  const availableStates = [
    ...new Set(Object.values(venues).map((v) => v.bizState).filter(Boolean)),
  ].sort();

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }}>Loading refund data...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <TabDescription>
        Refund activity aggregated per venue from the Orders Firestore collection. Shows total refund counts,
        weekly averages, refund values, and last refund dates. Filter by date range and state to spot venues
        with unusually high refund rates. Investigate repeat offenders for quality or fulfilment issues and
        follow up with venue owners as needed.
      </TabDescription>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date Range"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="all">All time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select
                  value={stateFilter}
                  label="State"
                  onChange={(e) => setStateFilter(e.target.value)}
                >
                  <MenuItem value="all">All states</MenuItem>
                  {availableStates.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MoneyOff sx={{ fontSize: 40, color: 'error.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Refunds
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {totalRefunds}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Refunds This Week
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {refundsThisWeek}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Refunds This Month
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {refundsThisMonth}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Refund Value
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {formatCurrency(totalRefundValue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Per-venue Table */}
      {venueRows.length === 0 ? (
        <Alert severity="info">No refunded orders found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="Refund tracking by venue">
            <TableHead>
              <TableRow>
                <TableCell scope="col" />
                <TableCell scope="col">
                  <TableSortLabel
                    active={sortField === 'bizName'}
                    direction={sortField === 'bizName' ? sortDir : 'asc'}
                    onClick={() => handleSort('bizName')}
                  >
                    Venue Name
                  </TableSortLabel>
                </TableCell>
                <TableCell scope="col" align="right">
                  <TableSortLabel
                    active={sortField === 'totalRefunds'}
                    direction={sortField === 'totalRefunds' ? sortDir : 'desc'}
                    onClick={() => handleSort('totalRefunds')}
                  >
                    Total Refunds
                  </TableSortLabel>
                </TableCell>
                <TableCell scope="col" align="right">
                  <TableSortLabel
                    active={sortField === 'refundsThisWeek'}
                    direction={sortField === 'refundsThisWeek' ? sortDir : 'desc'}
                    onClick={() => handleSort('refundsThisWeek')}
                  >
                    Refunds This Week
                  </TableSortLabel>
                </TableCell>
                <TableCell scope="col" align="right">
                  <TableSortLabel
                    active={sortField === 'avgRefundsPerWeek'}
                    direction={sortField === 'avgRefundsPerWeek' ? sortDir : 'desc'}
                    onClick={() => handleSort('avgRefundsPerWeek')}
                  >
                    Avg Refunds/Week
                  </TableSortLabel>
                </TableCell>
                <TableCell scope="col" align="right">
                  <TableSortLabel
                    active={sortField === 'totalRefundValue'}
                    direction={sortField === 'totalRefundValue' ? sortDir : 'desc'}
                    onClick={() => handleSort('totalRefundValue')}
                  >
                    Total Refund Value
                  </TableSortLabel>
                </TableCell>
                <TableCell scope="col">
                  <TableSortLabel
                    active={sortField === 'lastRefundDate'}
                    direction={sortField === 'lastRefundDate' ? sortDir : 'desc'}
                    onClick={() => handleSort('lastRefundDate')}
                  >
                    Last Refund Date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {venueRows.map((venue) => (
                <VenueRow key={venue.bizId} venue={venue} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RefundTracking;
